import React from 'react';
import { useLocation } from 'react-router-dom';
import useEditorStore from '../stores/useEditorStore';
import { useWorkflowState } from '../stores/useWorkflowStore';
import { useShallow } from 'zustand/shallow';
import styles from './ToolRenderer.module.css';

import changeCameraDirection from '../utils/CameraTransformValues';

// Import tool components
import RectificationTool from '../operations/rectification/RectificationTool';
import MeasurementTool from '../operations/MeasurementTool';
import WireframeToggle from './WireframeToggle';
import { 
  MovementToolCard, 
  ScissorsToolCard, 
  EraserToolCard, 
  PencilToolCard, 
  PinToolCard, 
  MeasureReductionToolCard, 
  ScoopRechargeToolCard, 
  BlockingZoneToolCard, 
  GlobalReductionToolCard, 
  SmoothningToolCard, 
  ElongationToolCard, 
  TrimlineToolCard,
  ThicknessToolCard,
  LockingSystemToolCard,
  ValveToolCard,
  PadsToolCard,
  FlareToolCard,
  FlaresSmoothningToolCard
} from './tools';

const ToolRenderer = () => {
  const location = useLocation();
  const isFinalPreviewStep = location.pathname === '/design/scan/final-preview';
  const isFinalizationStep = location.pathname === '/design/main/finalization';

  const [activeTool, toolSettings, undo, redo, resetScene] = useEditorStore(
    useShallow((state) => [state.activeTool, state.toolSettings, state.undo, state.redo, state.resetScene])
  );

  const patientData = useWorkflowState(
    useShallow((state) => state.patientData)
  );

  // Get amputation side and determine if we should swap Lateral/Mid labels
  const amputationSide = patientData?.keyMeasurements?.['Amputation Side'] || 'Right';
  const shouldSwapLabels = amputationSide === 'Left';

  // Render the appropriate tool based on activeTool
  const renderTool = () => {
    switch (activeTool) {
      // Scan Process - Axes Alignment
      case 'scan_axes_pin':
        return <PinToolCard />;
      case 'scan_axes_movement':
        return <MovementToolCard />;
        
      // Main Process - Rectification
      case 'main_rectification_measure_reduction':
        return <MeasureReductionToolCard />;
      case 'main_rectification_scoop_recharge':
        return <ScoopRechargeToolCard />;
      case 'main_rectification_blocking_zone':
        return <BlockingZoneToolCard />;
      case 'main_rectification_global_reduction':
        return <GlobalReductionToolCard />;
      case 'main_rectification_smoothning':
        return <SmoothningToolCard />;
      case 'scan_preparing_scissors':
        return <ScissorsToolCard />;
      case 'scan_preparing_eraser':
        return <EraserToolCard />;
      case 'scan_preparing_pencil':
        return <PencilToolCard />;
      case 'ruler':
        return (
          <div className={styles.toolOverlay}>
            <h4>Ruler Tool</h4>
            <p>Measure distances on the model</p>
          </div>
        );
      case 'rotate':
        return (
          <div className={styles.toolOverlay}>
            <h4>Rotate Tool</h4>
            <p>Rotate the model or parts</p>
          </div>
        );
      case 'grid':
        return (
          <div className={styles.toolOverlay}>
            <h4>Grid Tool</h4>
            <p>Show alignment grid</p>
          </div>
        );
      case 'smooth':
        return (
          <div className={styles.toolOverlay}>
            <h4>Smooth Tool</h4>
            <p>Smooth the model surface</p>
          </div>
        );
      case 'extend':
        return (
          <div className={styles.toolOverlay}>
            <h4>Extend Tool</h4>
            <p>Extend the model in selected direction</p>
          </div>
        );
      case 'preview':
        return (
          <div className={styles.toolOverlay}>
            <h4>Preview Tool</h4>
            <p>Preview changes before applying</p>
          </div>
        );
      case 'cut':
        return (
          <div className={styles.toolOverlay}>
            <h4>Cut Tool</h4>
            <p>Cut along defined lines</p>
          </div>
        );
      case 'line':
        return (
          <div className={styles.toolOverlay}>
            <h4>Line Tool</h4>
            <p>Draw lines on the model</p>
          </div>
        );
      case 'flare':
        return (
          <div className={styles.toolOverlay}>
            <h4>Flare Tool</h4>
            <p>Add flares to the socket</p>
          </div>
        );
      case 'adjust':
        return (
          <div className={styles.toolOverlay}>
            <h4>Adjust Tool</h4>
            <p>Fine-tune adjustments</p>
          </div>
        );
      case 'thickness':
        return (
          <div className={styles.toolOverlay}>
            <h4>Thickness Tool</h4>
            <p>Set wall thickness: {toolSettings.thickness?.value || 3.0}mm</p>
          </div>
        );
      case 'lock':
        return (
          <div className={styles.toolOverlay}>
            <h4>Lock Tool</h4>
            <p>Add locking mechanism</p>
          </div>
        );
      case 'position':
        return (
          <div className={styles.toolOverlay}>
            <h4>Position Tool</h4>
            <p>Position elements precisely</p>
          </div>
        );
      case 'align':
        return (
          <div className={styles.toolOverlay}>
            <h4>Align Tool</h4>
            <p>Align model elements</p>
          </div>
        );
      case 'valve':
        return (
          <div className={styles.toolOverlay}>
            <h4>Valve Tool</h4>
            <p>Add valve to socket</p>
          </div>
        );
      case 'pad':
        return (
          <div className={styles.toolOverlay}>
            <h4>Pad Tool</h4>
            <p>Add comfort pads</p>
          </div>
        );
      case 'review':
        return (
          <div className={styles.toolOverlay}>
            <h4>Review Tool</h4>
            <p>Review the final design</p>
          </div>
        );
      case 'export':
        return (
          <div className={styles.toolOverlay}>
            <h4>Export Tool</h4>
            <p>Export the final design</p>
          </div>
        );
      case 'save':
        return (
          <div className={styles.toolOverlay}>
            <h4>Save Tool</h4>
            <p>Save current progress</p>
          </div>
        );
      case 'main_elongation_elongation':
        return <ElongationToolCard />;
      case 'main_trimline_trimline':
        return <TrimlineToolCard />;
      case 'main_thickness_thickness':
        return <ThicknessToolCard />;
      case 'main_locking_lock':
        return <LockingSystemToolCard />;
      case 'main_valve_valve':
        return <ValveToolCard />;
      case 'main_pads_pad':
        return <PadsToolCard />;
      case 'main_flares_flare':
        return <FlareToolCard />;
      case 'main_flares_smoothning':
        return <FlaresSmoothningToolCard />;
      default:
        return null;
    }
  };

  const onDirectionButtonClick = (direction) => {
    changeCameraDirection(direction);
  };

  return (
    <>
      {/* Always show wireframe toggle */}
      {/* <WireframeToggle /> */}
      
      {/* Render the active tool */}
      {renderTool()}
      
      {/* Direction View Control - Top Right */}
      {!isFinalPreviewStep && (
        <div className={styles.directionControl}>
          <img
            src="/other_icons/canvas-direction.png"
            alt="Direction Control"
            className={styles.directionIcon}
          />
          <div className={styles.directionButtons}>
            <button onClick={() => onDirectionButtonClick('upper')} className={`${styles.directionButton} ${styles.directionButtonUpper}`}>Upper</button>
            <button onClick={() => onDirectionButtonClick('lateral')} className={`${styles.directionButton} ${styles.directionButtonLateral}`}>{shouldSwapLabels ? 'Mid' : 'Lateral'}</button>
            <button onClick={() => onDirectionButtonClick('front')} className={`${styles.directionButton} ${styles.directionButtonFront}`}>Front</button>
            <button onClick={() => onDirectionButtonClick('rear')} className={`${styles.directionButton} ${styles.directionButtonRear}`}>Rear</button>
            <button onClick={() => onDirectionButtonClick('mid')} className={`${styles.directionButton} ${styles.directionButtonMid}`}>{shouldSwapLabels ? 'Lateral' : 'Mid'}</button>
            <button onClick={() => onDirectionButtonClick('bottom')} className={`${styles.directionButton} ${styles.directionButtonBottom}`}>Bottom</button>
          </div>
        </div>
      )}

      {/* View Controls - Bottom Right of 3D Canvas */}
      {!isFinalizationStep && !isFinalPreviewStep && (
      <div className='absolute bottom-[10px] right-[10px] flex gap-[8px]'>
        <button onClick={undo} title="Undo" className='view-control-button'>
          <img src="/other_icons/canvas-left-rotate.png" alt="Undo" className='w-4 h-4 filter brightness-0 invert'/>
        </button>

        <button onClick={redo} title="Redo" className='view-control-button'>
          <img src="/other_icons/canvas-right-rotate.png" alt="Redo" className='w-4 h-4 filter brightness-0 invert'/>
        </button>

        <button title="Fullscreen" className='view-control-button'>
          <img src="/other_icons/canvas-zoom.png" alt="Fullscreen" className='w-4 h-4 filter brightness-0 invert'/>
        </button>
      </div>
      )}
      
      {/* Question Button - Bottom of Left Toolbar */}
      {!isFinalizationStep && !isFinalPreviewStep && (
      <button 
        title="Help"
        className={styles.questionButton}
      >
        ?
      </button>
      )}
    </>
  );
};

export default ToolRenderer; 